import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useListRetrieval,
  useListEdition,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Assignable, ProjectElementType } from "@planzone/dto";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { clip } from "@common/clipeable";
import { Loader } from "@common/loader";
import { TagList } from "@common/tagList";
import { Safeguard } from "@scenes/safeguard";
import { ElementAttachments } from "@common/elementAttachments";
import { Comments } from "@scenes/comments";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Modal } from "@common/modal";
import { ProjectRedirection } from "@common/projectRedirection";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { useModal } from "@root/client/modal";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { ZoneAvatar } from "@common/zoneAvatar";
import { Props } from "./types";
import { Details } from "./details";
import { Timestamp } from "./timestamp";
import styles from "./styles.scss";

function List({ listId, isMultiProject, close }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestFailure,
    list,
    hasRetrievedList,
    retrieveList,
  } = useListRetrieval(listId);
  const { pushView } = useModal();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    list.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const {
    isRequestPending: isEditRequestPending,
    editList,
    error,
  } = useListEdition();

  React.useEffect(() => {
    retrieveList(listId);
  }, [listId]);

  return (
    <WorkspaceContext type="project" project={list.project}>
      <Safeguard
        useCases={[UseCases.editList, UseCases.postComment]}
        context={{ list }}
      >
        {({ canEditList, canPostComment }) => (
          <div
            className={classNames(
              { [styles.list]: !isRequestPending },
              { [styles.listLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : (
              <Modal
                headerClassName={styles.header}
                closeIconClassName={styles.icon}
                header={
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        onNavigate={close}
                        breadcrumbs={[
                          {
                            name: list.zone.name,
                            to: `/zones/${list.zone.uuid}`,
                            icon: (
                              <ZoneAvatar
                                name={list.zone.name}
                                color={list.zone.color}
                              />
                            ),
                          },
                          {
                            name: list.project.name,
                            to: `/projects/${list.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          { name: list.name },
                        ]}
                      />
                    </h1>

                    <div
                      className={classNames(styles.rightContent, {
                        [styles.rightContentMultiProject]: isMultiProject,
                      })}
                    >
                      {isMultiProject && (
                        <ProjectRedirection
                          className={styles.contentProjectRedirection}
                          projectId={list.project.uuid}
                          close={close}
                        />
                      )}
                      <div
                        className={classNames(styles.contextualMenu, {
                          [styles.contextualMenuMultiProject]: isMultiProject,
                        })}
                      >
                        <SecureContextualMenu
                          context={{ list }}
                          items={[
                            {
                              name: t("listRetrieval.copyUrl"),
                              useCase: UseCases.retrieveList,
                              act: () =>
                                clip(
                                  `${location.origin}/lists/${list.uuid}`,
                                  null,
                                ),
                            },
                            {
                              name: t("listRetrieval.move"),
                              useCase: UseCases.moveListElement,
                              act: () =>
                                pushView({
                                  key: UseCases.moveListElement,
                                  data: {
                                    projectElementId: list.uuid,
                                    projectElementType: ProjectElementType.LIST,
                                    currentParentList: list.parentList || list,
                                    project: list.project,
                                  },
                                }),
                            },
                            {
                              name: t("listRetrieval.remove"),
                              useCase: UseCases.removeList,
                              act: () =>
                                pushView({
                                  key: UseCases.removeList,
                                  data: { list },
                                }),
                            },
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                }
                close={close}
              >
                {isRequestFailure ? (
                  <div className={styles.scenePlaceholder}>
                    <ScenePlaceholder goBack={close} />
                  </div>
                ) : (
                  <div className={styles.contentModal}>
                    <div className={styles.main}>
                      <div className={styles.content}>
                        <div className={styles.scrollWrapper}>
                          <div className={styles.details}>
                            <Details
                              list={list}
                              error={error}
                              readOnly={!canEditList}
                            />
                          </div>
                          <div className={styles.tagList}>
                            <TagList
                              id={list.uuid}
                              tags={list.tags}
                              type={Assignable.LIST}
                              project={list.project}
                              readOnly={!canEditList}
                            />
                          </div>
                          <Safeguard
                            useCases={[
                              UseCases.editProjectElementDetailsConfiguration,
                            ]}
                            context={{ list }}
                          >
                            {({ canEditProjectElementDetailsConfiguration }) =>
                              (template.length > 0 ||
                                canEditProjectElementDetailsConfiguration) && (
                                <div className={styles.secondaryCriteria}>
                                  <div className={styles.row}>
                                    <p className={styles.label}>
                                      {t("listRetrieval.secondaryCriteria")}
                                    </p>

                                    {canEditProjectElementDetailsConfiguration ? (
                                      <div
                                        className={styles.configurationIconBox}
                                        onClick={() =>
                                          pushView({
                                            key: UseCases.retrieveProjectElementDetailsConfiguration,
                                            data: { project: list.project },
                                          })
                                        }
                                      >
                                        <CogWheelIcon
                                          className={styles.configurationIcon}
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className={styles.contextualMenuIcon}
                                      />
                                    )}
                                  </div>
                                  <ProjectElementsSecondaryCriteria
                                    project={list.project}
                                    criteriaForms={template.reduce(
                                      (acc, criteria) => ({
                                        ...acc,
                                        [criteria.uuid]: (
                                          <SecondaryCriteriaForm
                                            criteria={criteria}
                                            value={
                                              list.attributes
                                                ? list.attributes[
                                                    criteria.uuid
                                                  ] || ""
                                                : ""
                                            }
                                            flexible
                                            disabled={isEditRequestPending}
                                            submit={(value) =>
                                              editList({
                                                list,
                                                nextList: {
                                                  ...list,
                                                  attributes: {
                                                    ...list.attributes,
                                                    [criteria.uuid]: value,
                                                  },
                                                },
                                              })
                                            }
                                          />
                                        ),
                                      }),
                                      {},
                                    )}
                                  />
                                </div>
                              )
                            }
                          </Safeguard>
                          <ElementAttachments
                            element={list}
                            project={list.project}
                            elementType="LIST"
                            readOnly={!canEditList}
                          />
                        </div>
                      </div>
                      <div className={styles.timeline}>
                        <div className={styles.timestamp}>
                          <Timestamp
                            list={list}
                            error={error}
                            readOnly={!canEditList}
                          />
                        </div>
                        <div className={styles.comments}>
                          {!list.project.isTemplate && (
                            <Comments
                              subject={list.uuid}
                              type="list"
                              canPost={canPostComment}
                              project={list.project.uuid}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Modal>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { List };
